/** @jsxImportSource @emotion/react */
import { useEffect, useRef, useState } from 'react'
import { css } from '@emotion/react'
import { Document, Page, pdfjs } from 'react-pdf'
import { Button, Modal, Spinner } from 'react-bootstrap'

import Toast from 'components/atoms/Toast'
import * as api from 'helpers/api'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`

type PrintOptionKey = 'primary-full' | 'primary-data' | 'cob-full' | 'cob-data' | 'tertiary-full' | 'tertiary-data'

type PrintOption = {
  key: PrintOptionKey
  label: string
  description: string
}

const PRIMARY_OPTIONS: PrintOption[] = [
  {
    key: 'primary-full',
    label: 'Primary claim — full form',
    description: 'Complete CMS-1500 background with the filled-in fields.',
  },
  {
    key: 'primary-data',
    label: 'Primary claim — data only',
    description: 'Values only, for overlay printing onto a pre-printed form.',
  },
]

// COB forms swap the payers: the destination payer fills the insured boxes and
// the most recent prior payer moves to the other-insured boxes, with box 29
// showing what the payers before it paid.
const COB_OPTIONS: PrintOption[] = [
  {
    key: 'cob-full',
    label: 'COB claim (secondary) — full form',
    description: 'Complete form addressed to the secondary payer, with the primary as other insured.',
  },
  {
    key: 'cob-data',
    label: 'COB claim (secondary) — data only',
    description: 'Secondary-payer values only, for overlay printing.',
  },
]

const TERTIARY_OPTIONS: PrintOption[] = [
  {
    key: 'tertiary-full',
    label: 'Tertiary COB claim — full form',
    description: 'Complete form addressed to the tertiary payer, with the secondary as other insured '
      + 'and box 29 carrying both prior payments.',
  },
  {
    key: 'tertiary-data',
    label: 'Tertiary COB claim — data only',
    description: 'Tertiary-payer values only, for overlay printing.',
  },
]

interface Props {
  chargeId: string
  patientId: string
  show: boolean
  onClose: () => void
  // Adds the COB print options; only meaningful when the charge has a secondary payer.
  allowCob?: boolean
  // Adds the tertiary COB print options; only meaningful when the chain has a position-3 payer.
  allowTertiary?: boolean
}

export const CMS1500DownloadModal = ({
  chargeId, patientId, show, onClose, allowCob = false, allowTertiary = false,
}: Props) => {
  const [optionKey, setOptionKey] = useState<PrintOptionKey>('primary-full')
  const payerRank = optionKey.startsWith('tertiary')
    ? 'tertiary'
    : (optionKey.startsWith('cob') ? 'secondary' : 'primary')
  const isCob = payerRank !== 'primary'
  const dataOnly = optionKey.endsWith('data')
  const printOptions = [
    ...PRIMARY_OPTIONS,
    ...(allowCob ? COB_OPTIONS : []),
    ...(allowTertiary ? TERTIARY_OPTIONS : []),
  ]
  const [pdfUrl, setPdfUrl] = useState('')
  const [numPages, setNumPages] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!show) {
      setOptionKey('primary-full')
      setNumPages(null)
      setError('')
      setPdfUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev)
        }
        return ''
      })
    }
  }, [show])

  useEffect(() => {
    if (!show) {
      return
    }

    let disposed = false
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    const loadPreview = async () => {
      setIsLoading(true)
      setError('')

      try {
        const result = await api.get(
          `/lab/patients/${patientId}/account/charges/${chargeId}/cms1500`,
          {
            ...(dataOnly ? { dataOnly: true } : {}),
            ...(isCob ? { payerRank } : {}),
          },
          true,
        )
        const nextUrl = URL.createObjectURL(new Blob([result], { type: 'application/pdf' }))

        if (disposed || requestId !== requestIdRef.current) {
          URL.revokeObjectURL(nextUrl)
          return
        }

        setPdfUrl((prev) => {
          if (prev) {
            URL.revokeObjectURL(prev)
          }
          return nextUrl
        })
        setNumPages(null)
      } catch (err) {
        const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        if (!disposed && requestId === requestIdRef.current) {
          setError(message || 'Unable to load CMS-1500 preview')
        }
      } finally {
        if (!disposed && requestId === requestIdRef.current) {
          setIsLoading(false)
        }
      }
    }

    loadPreview()

    return () => {
      disposed = true
    }
  }, [chargeId, dataOnly, isCob, payerRank, patientId, show])

  const onDocumentLoadSuccess = ({ numPages: loadedPages }: { numPages: number }) => {
    setNumPages(loadedPages)
  }

  const handleDownload = () => {
    if (!pdfUrl) {
      return
    }

    const link = document.createElement('a')
    link.href = pdfUrl
    link.setAttribute(
      'download',
      `CMS1500${payerRank === 'tertiary' ? '_TertiaryCOB' : (isCob ? '_COB' : '')}`
        + `_Charge_${chargeId}${dataOnly ? '_DataOnly' : ''}.pdf`,
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    Toast({ type: 'success', label: 'CMS1500 form downloaded successfully!' })
  }

  return (
    <Modal centered show={show} onHide={onClose} size="xl">
      <Modal.Header>
        <Modal.Title>Download CMS-1500 Form</Modal.Title>
      </Modal.Header>
      <Modal.Body
        css={css`
          display: flex;
          gap: 24px;
          min-height: 70vh;
          max-height: calc(100vh - 180px);
          overflow: hidden;

          @media (max-width: 992px) {
            flex-direction: column;
            min-height: auto;
            max-height: calc(100vh - 180px);
          }
        `}>
        <div
          css={css`
            width: 320px;
            flex-shrink: 0;
            border-right: 1px solid #dde3ea;
            padding-right: 24px;
            overflow-y: auto;

            @media (max-width: 992px) {
              width: 100%;
              border-right: none;
              border-bottom: 1px solid #dde3ea;
              padding-right: 0;
              padding-bottom: 20px;
              max-height: 220px;
            }
          `}>
          <div
            css={css`
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 8px;
            `}>
            Print Settings
          </div>
          <div
            css={css`
              color: #687280;
              font-size: 14px;
              line-height: 1.5;
              margin-bottom: 20px;
            `}>
            {allowCob
              ? 'Choose which claim to print and whether you want the full CMS-1500 form or only the populated data.'
              : 'Choose whether you want the full CMS-1500 form or only the populated data for printing.'}
          </div>

          <div
            css={css`
              display: grid;
              gap: 12px;
            `}>
            {printOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setOptionKey(option.key)}
                css={css`
                  text-align: left;
                  border-radius: 8px;
                  border: 1px solid ${optionKey === option.key ? '#2c83bd' : '#d9e0e7'};
                  background: ${optionKey === option.key ? 'rgba(44, 131, 189, 0.08)' : '#fff'};
                  padding: 14px 16px;
                  transition: border-color 0.15s ease, background 0.15s ease;

                  &:hover {
                    border-color: #2c83bd;
                  }
                `}>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 6px;
                  `}>
                  <span
                    css={css`
                      width: 16px;
                      height: 16px;
                      border-radius: 999px;
                      border: 2px solid ${optionKey === option.key ? '#2c83bd' : '#9aa5b1'};
                      display: inline-flex;
                      align-items: center;
                      justify-content: center;
                      flex-shrink: 0;

                      &::after {
                        content: '';
                        width: 8px;
                        height: 8px;
                        border-radius: 999px;
                        background: ${optionKey === option.key ? '#2c83bd' : 'transparent'};
                      }
                    `}
                  />
                  <span
                    css={css`
                      font-size: 15px;
                      font-weight: 600;
                      color: #1f2933;
                    `}>
                    {option.label}
                  </span>
                </div>
                <div
                  css={css`
                    color: #687280;
                    font-size: 13px;
                    line-height: 1.45;
                    padding-left: 26px;
                  `}>
                  {option.description}
                </div>
              </button>
            ))}
          </div>

          <Button
            variant="primary"
            type="button"
            disabled={!pdfUrl || isLoading}
            onClick={handleDownload}
            css={css`
              width: 100%;
              margin-top: 20px;
            `}>
            Download
          </Button>
        </div>

        <div
          css={css`
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
          `}>
          <div
            css={css`
              font-size: 18px;
              font-weight: 600;
              margin-bottom: 12px;
            `}>
            Preview
          </div>
          <div
            css={css`
              flex: 1;
              overflow-y: auto;
              background: #eef2f6;
              border-radius: 10px;
              padding: 16px;
              min-height: 0;
            `}>
            {isLoading && (
              <div
                css={css`
                  min-height: 420px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #687280;
                  gap: 10px;
                `}>
                <Spinner animation="border" size="sm" />
                <span>Loading CMS-1500 preview...</span>
              </div>
            )}

            {!isLoading && error && (
              <div
                css={css`
                  min-height: 420px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  color: #b42318;
                  font-weight: 500;
                  text-align: center;
                  padding: 24px;
                `}>
                {error}
              </div>
            )}

            {!isLoading && !error && pdfUrl && (
              <Document
                file={pdfUrl}
                loading=""
                onLoadSuccess={onDocumentLoadSuccess}
                externalLinkTarget="_blank">
                <div
                  css={css`
                    display: grid;
                    gap: 16px;
                    justify-items: center;
                  `}>
                  {Array.from({ length: numPages || 0 }, (_, index) => index + 1).map((pageNumber) => (
                    <div
                      key={pageNumber}
                      css={css`
                        background: white;
                        box-shadow: 0 8px 24px rgba(16, 24, 40, 0.08);
                      `}>
                      <Page
                        pageNumber={pageNumber}
                        width={700}
                        loading=""
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </div>
                  ))}
                </div>
              </Document>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" type="button" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
